import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { reviewsAPI } from '../../services/api';
import { Review, ReviewModel } from '../../types';
import toast from 'react-hot-toast';

interface ReviewsState {
  reviews: Review[];
  userReviews: Review[];
  stats: {
    averageRating: number;
    totalReviews: number;
    ratingDistribution: { [key: number]: number };
  } | null;
  loading: boolean;
  createLoading: boolean;
  error: string | null;
  page: number;
  hasMore: boolean;
}

const initialState: ReviewsState = {
  reviews: [],
  userReviews: [],
  stats: null,
  loading: false,
  createLoading: false,
  error: null,
  page: 1,
  hasMore: true,
};

// Async thunks
export const fetchUserReviews = createAsyncThunk(
  'reviews/fetchUserReviews',
  async ({ userId, page = 1 }: { userId: string; page?: number }, { rejectWithValue }) => {
    try {
      const response = await reviewsAPI.getUserReviews(userId, page);
      return { reviews: response.data.data.reviews, page };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch reviews');
    }
  }
);

export const createReview = createAsyncThunk(
  'reviews/createReview',
  async (reviewData: ReviewModel[], { rejectWithValue }) => {
    try {
      const response = await reviewsAPI.createReview(reviewData);
      return response.data.data.review;
    } catch (error: any) {
      // console.log(error)
      return rejectWithValue(error.response?.data?.error || 'Failed to create review');
    }
  }
);

const reviewsSlice = createSlice({
  name: 'reviews',
  initialState,
  reducers: {
    clearReviews: (state) => {
      state.reviews = [];
      state.userReviews = [];
      state.stats = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch user reviews
      .addCase(fetchUserReviews.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserReviews.fulfilled, (state, action) => {
        state.loading = false;
        const { reviews, page } = action.payload;
        
        if (page === 1) {
          state.reviews = reviews;
        } else {
          state.reviews = [...state.reviews, ...reviews];
        }
        
        state.page = page;
        state.hasMore = reviews.length === 20;

        // Calculate stats (based on ALL loaded reviews)
        if (state.reviews.length > 0) {
          const totalRating = state.reviews.reduce((sum: number, review: Review) => sum + review.rating, 0);
          const averageRating = totalRating / state.reviews.length;
          
          const ratingDistribution = state.reviews.reduce((dist: { [key: number]: number }, review: Review) => {
            dist[review.rating] = (dist[review.rating] || 0) + 1;
            return dist;
          }, {});

          state.stats = {
            averageRating,
            totalReviews: state.reviews.length,
            ratingDistribution,
          };
        }
      })
      .addCase(fetchUserReviews.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        toast.error('Failed to load reviews');
      })
      
      // Create review
      .addCase(createReview.pending, (state) => {
        state.createLoading = true;
        state.error = null;
      })
      .addCase(createReview.fulfilled, (state, action) => {
        state.createLoading = false;
        state.userReviews.unshift(action.payload);
        toast.success('Review submitted successfully!');
      })
      .addCase(createReview.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload as string;
        toast.error('Failed to submit review');
      });
  },
});

export const { clearReviews, clearError } = reviewsSlice.actions;
export default reviewsSlice.reducer;
